import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, PageHeader, Table } from 'antd';
import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  PUBLISH_ERROR_TYPE,
  PublishModalError,
} from 'app/components/commons/PublishModalError/PublishModalError';
import { PaginatedSearchHeader } from 'app/components/commons/Range/PaginatedSearchHeader/PaginatedSearchHeader';
import { Pagination } from 'app/components/commons/Range/Pagination/Pagination';
import {
  IsLegacyFilterOption,
  ViewModeRadio,
} from 'app/components/commons/ViewModeRadio/ViewModeRadio';
import { CoverCellRoom, PublishedCell } from 'app/components/lists/cells';
import { ModalCheckBox } from 'app/components/pages/Extranet/ExtranetBookings/ModalCheckBox';
import { usePublishRoom } from 'app/hooks/data/core/admin/rooms/usePublishRoom';
import { useRooms } from 'app/hooks/data/core/admin/rooms/useRooms';
import { useUnpublishRoom } from 'app/hooks/data/core/admin/rooms/useUnpublishRoom';
import { usePublishOldRoom } from 'app/hooks/data/rooms/usePublishOldRoom';
import type { RoomStatus as RoomStatusType } from 'app/typings/core/admin/rooms/enum/roomStatus';
import { ROOM_STATUS } from 'app/typings/core/admin/rooms/enum/roomStatus';
import type { RoomListElement } from 'app/typings/core/admin/rooms/roomList';
import { dateAndHour } from 'app/utils/dates';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { extractMissingFields } from 'app/utils/publishError';
import {
  extractBoolean,
  parseArrayParam,
  useQueryParamHistory,
} from 'app/utils/queryParamHistory';
import type { SortOrder } from 'app/utils/sort';
import {
  convertFromAntdSortOrder,
  convertToAntdSortOrder,
  formatSortingForAPI,
  getSortOrder,
} from 'app/utils/sort';

import 'styles/layout/page-list.scss';

import { PAGE_SIZE } from '../../CommercialGestures/CommercialGestures';

import './Rooms.scss';

const { Content } = Layout;
const { Column } = Table;

const formatIsPublished = (
  isPublishedFilter?: boolean[]
): ROOM_STATUS[] | undefined => {
  if (!isPublishedFilter || isPublishedFilter.length === 0) {
    return undefined;
  }

  const hasPublished = isPublishedFilter.includes(true);
  const hasUnpublished = isPublishedFilter.includes(false);

  if (hasPublished && hasUnpublished) {
    return undefined;
  }

  return isPublishedFilter.map((val) =>
    val ? ROOM_STATUS.PUBLISHED : ROOM_STATUS.UNPUBLISHED
  );
};

type RoomsListQueryParam = {
  search?: string;
  pageNumber?: number;
  dateSorting?: SortOrder;
  sortingColumn?: 'CREATED_AT' | 'UPDATED_AT' | 'NAME';
  isPublishedFilter?: boolean[];
  isLegacyFilter?: IsLegacyFilterOption;
};

const queryParamInit = {
  pageNumber: 1,
  dateSorting: 'descend',
  sortingColumn: 'UPDATED_AT',
  isPublishedFilter: [true, false],
  isLegacyFilter: IsLegacyFilterOption.CURRENT,
} as RoomsListQueryParam;

const queryParamKeys = {
  pageNumber: 'p',
  dateSorting: 'ds',
  isPublishedFilter: 'pu',
  search: 's',
  isLegacyFilter: 'il',
};
const queryParamFormatter = {};
const queryParamExtractor = {
  pageNumber: parseInt,
  dateSorting: getSortOrder,
  isPublishedFilter: parseArrayParam(extractBoolean),
  isLegacyFilter: (v: string) => v as IsLegacyFilterOption,
};

export const Rooms = () => {
  const history = useHistory();
  const location = useLocation();

  const { queryParam, updateQueryParam } =
    useQueryParamHistory<RoomsListQueryParam>(
      queryParamInit,
      queryParamKeys,
      queryParamFormatter,
      queryParamExtractor
    );

  const debouncedQueryParams = useDebounce(queryParam, 500);

  const makePayload = useCallback(() => {
    const sortingOrder =
      debouncedQueryParams.dateSorting &&
      debouncedQueryParams.dateSorting !== 'none'
        ? formatSortingForAPI(debouncedQueryParams.dateSorting)
        : undefined;

    const sortingColumn = debouncedQueryParams.sortingColumn;

    return {
      search: debouncedQueryParams.search,
      sortingOrder,
      sortingColumn,
      pageNumber: debouncedQueryParams.pageNumber || 1,
      status: formatIsPublished(debouncedQueryParams.isPublishedFilter),
      isLegacyFilter: debouncedQueryParams.isLegacyFilter,
    };
  }, [debouncedQueryParams]);

  const {
    data: searchRoomResponse,
    isFetching: loading,
    refetch: reloadRooms,
  } = useRooms(makePayload(), { enabled: !!debouncedQueryParams });

  const { mutateAsync: publishOldRoom } = usePublishOldRoom();
  const { mutateAsync: publishRoom } = usePublishRoom();
  const { mutateAsync: unpublishRoom } = useUnpublishRoom();

  const [missingFieldsError, setMissingFieldsError] = useState<string[] | null>(
    null
  );
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const handlePageNumber = (pageNumber: number) =>
    updateQueryParam({ pageNumber });

  const handleSearch = (search: string) =>
    updateQueryParam({ pageNumber: 1, search });

  const handleIsPublishedFilter = (statuses: boolean[]) => {
    if (!statuses.length) {
      updateQueryParam({ isPublishedFilter: undefined, pageNumber: 1 });
    } else {
      updateQueryParam({ isPublishedFilter: statuses, pageNumber: 1 });
    }
  };

  const goToEditPage = (id: number, isLegacy: boolean) => {
    const pathname = isLegacy ? `/rooms/legacy/${id}` : `/rooms/${id}`;

    history.push({
      pathname,
      state: { pathname: location.pathname + location.search },
    });
  };

  const handleRowActions = ({ id, isLegacy }: RoomListElement) => ({
    onClick: () => goToEditPage(id, isLegacy),
  });

  const actionMenu = (room: RoomListElement) => (
    <div className="actions-menu">
      <Dropdown
        menu={{
          items: [
            {
              key: 'edit',
              label: 'Edit',
              onClick: () => goToEditPage(room.id, room.isLegacy),
            },
            ...(room.status === ROOM_STATUS.UNPUBLISHED ||
            room.status === ROOM_STATUS.PUBLISHED
              ? [
                  {
                    key:
                      room.status === ROOM_STATUS.PUBLISHED
                        ? 'unpublish'
                        : 'publish',
                    label:
                      room.status === ROOM_STATUS.PUBLISHED
                        ? 'Unpublish'
                        : 'Publish',
                    onClick: async () => {
                      try {
                        if (room.isLegacy) {
                          await publishOldRoom(room.id.toString());
                        } else if (room.status === ROOM_STATUS.PUBLISHED) {
                          await unpublishRoom({
                            roomId: room.id.toString(),
                            hotelId: room.hotelId,
                          });
                        } else {
                          await publishRoom({
                            roomId: room.id.toString(),
                            hotelId: room.hotelId,
                          });
                        }

                        await reloadRooms();
                      } catch (err) {
                        const missingFields = extractMissingFields(err);

                        if (missingFields !== null) {
                          setMissingFieldsError(missingFields);
                          setIsErrorModalVisible(true);
                        }
                      }
                    },
                  },
                ]
              : []),
          ],
        }}
        trigger={['click']}
      >
        <EllipsisOutlined rotate={90} />
      </Dropdown>
    </div>
  );

  const newRoomButton = (
    <Button
      type="primary"
      size="large"
      onClick={() =>
        history.push({
          pathname: 'rooms/new',
          state: { pathname: location.pathname + location.search },
        })
      }
    >
      New
    </Button>
  );

  const isLastPage =
    !!searchRoomResponse && searchRoomResponse.data.length < PAGE_SIZE;

  const selectedIsLegacyFilterOption =
    queryParam.isLegacyFilter ?? IsLegacyFilterOption.ALL;

  const renderHeader = () => (
    <PaginatedSearchHeader
      onSearch={handleSearch}
      currentSearch={queryParam.search}
      currentPage={queryParam.pageNumber || 1}
      setPageNumber={handlePageNumber}
      isLastPage={isLastPage ?? false}
    />
  );

  return (
    <Layout className="page-list room-list">
      <PageHeader className="header" title="Rooms" extra={newRoomButton} />
      <Content className="body">
        <Table<RoomListElement>
          dataSource={searchRoomResponse?.data ?? []}
          pagination={false}
          title={renderHeader}
          loading={loading}
          rowKey="id"
          onRow={handleRowActions}
          onChange={(__, ___, sorter) => {
            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const { order, columnKey } = singleSorter;

            updateQueryParam({
              dateSorting: convertFromAntdSortOrder(order),
              sortingColumn: columnKey as 'CREATED_AT' | 'UPDATED_AT' | 'NAME',
              pageNumber: 1,
            });
          }}
        >
          <Column<RoomListElement>
            title="Name"
            dataIndex="categoryName"
            render={(category, entity) => (
              <CoverCellRoom category={category} entity={entity} />
            )}
          />
          <Column title="Hotel" dataIndex="hotelName" />
          <Column<RoomListElement>
            title="Data Model"
            dataIndex="isLegacy"
            filterDropdown={() => (
              <ViewModeRadio<IsLegacyFilterOption>
                value={selectedIsLegacyFilterOption}
                onChange={(selected) => {
                  updateQueryParam({
                    isLegacyFilter: selected,
                    pageNumber: 1,
                  });
                }}
                options={[
                  {
                    value: IsLegacyFilterOption.CURRENT,
                    label: 'Current hotel mode',
                  },
                  {
                    value: IsLegacyFilterOption.ALL,
                    label: 'All hotel modes',
                  },
                ]}
              />
            )}
            render={(isLegacy: boolean) => (isLegacy ? 'Old' : 'New')}
          />

          <Column
            key="UPDATED_AT"
            title="Last modified"
            dataIndex="updatedAt"
            sorter
            sortOrder={
              queryParam.dateSorting &&
              queryParam.sortingColumn === 'UPDATED_AT'
                ? convertToAntdSortOrder(queryParam.dateSorting)
                : undefined
            }
            render={dateAndHour}
          />
          <Column
            title={
              <ModalCheckBox
                title="Status"
                defaultValue={[true, false]}
                onChange={handleIsPublishedFilter}
                items={[
                  { value: true, label: 'Published' },
                  { value: false, label: 'Unpublished' },
                ]}
                value={queryParam.isPublishedFilter}
              />
            }
            dataIndex="status"
            render={(status: RoomStatusType, record: RoomListElement) =>
              PublishedCell(status === ROOM_STATUS.PUBLISHED, record)
            }
            className="sort-column"
          />
          <Column
            render={actionMenu}
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
        </Table>
        <Pagination
          className="footer"
          currentPage={queryParam.pageNumber || 1}
          setPageNumber={handlePageNumber}
          isLastPage={isLastPage ?? false}
        />
        <PublishModalError
          type={PUBLISH_ERROR_TYPE.ROOM}
          missingFields={missingFieldsError ?? []}
          isOpen={isErrorModalVisible}
          handleOk={() => setIsErrorModalVisible(false)}
        />
      </Content>
    </Layout>
  );
};
